import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useActiveRunsCount } from '../hooks/useActiveRunsCount';
import { isFirstCompletedAnalysis } from '../services/videoAnalysisSupabaseService';
import { buildMetricsPath } from '../lib/deepVideoAnalysisRoutes';

/**
 * Headless notifier mounted once at the app level.
 * When the active-runs count transitions from > 0 to 0 we surface a toast
 * with a "View →" action so users who navigated away from `/status` learn
 * their analysis is ready. The inline banner on `/status` covers the case
 * where the user is already there, so we suppress the toast on that route.
 */
const AnalysisCompletionNotifier: React.FC = () => {
  const { currentUser } = useAuth();
  const { activeCount, activeRun } = useActiveRunsCount();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const prevActiveCountRef = useRef<number>(0);
  const prevActiveRunRef = useRef<typeof activeRun>(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    if (!currentUser) {
      isInitialLoadRef.current = true;
      prevActiveCountRef.current = 0;
      prevActiveRunRef.current = null;
      return;
    }

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      prevActiveCountRef.current = activeCount;
      prevActiveRunRef.current = activeRun;
      return;
    }

    const wasActive = prevActiveCountRef.current > 0;
    const nowIdle = activeCount === 0;
    const lastRun = prevActiveRunRef.current;

    if (wasActive && nowIdle && location.pathname !== '/status' && currentUser?.uid) {
      const navState: { fromCompletion: true; requestId?: string; selectedFile?: { video_name: string } } = { fromCompletion: true };
      if (lastRun?.requestId) navState.requestId = lastRun.requestId;
      if (lastRun?.videoName) navState.selectedFile = { video_name: lastRun.videoName };
      const metricsPath = buildMetricsPath({
        video: lastRun?.videoName,
        requestId: lastRun?.requestId,
      });

      void (async () => {
        const firstRun = await isFirstCompletedAnalysis(currentUser.uid);
        if (firstRun) {
          navigate(metricsPath, { state: navState });
          return;
        }

        addToast({
          type: 'success',
          message: lastRun?.videoName
            ? `Analysis ready for "${lastRun.videoName}"`
            : 'Your analysis is ready.',
          duration: 8000,
          action: {
            label: 'View',
            onClick: () => navigate(metricsPath, { state: navState })
          }
        });
      })();
    }

    prevActiveCountRef.current = activeCount;
    prevActiveRunRef.current = activeRun;
  }, [activeCount, activeRun, currentUser, addToast, navigate, location.pathname]);

  return null;
};

export default AnalysisCompletionNotifier;
